import { createSignal } from 'solid-js'

import { translate } from '../i18n'
import {
   dispatchCurrentFastStateChange,
   dispatchHistoryMonthsChange,
   type CurrentFastStateDetail,
} from '../runtimeBridge'
import { formatDateTimeLocalValue } from '../utils/time'
import { createEditModalState } from './editModalState'

const HOUR_IN_MS = 60 * 60 * 1000
const FAST_DURATION_STEP_MS = HOUR_IN_MS
const MIN_ACTIVE_FAST_DURATION_MS = HOUR_IN_MS
/** The shortest fast that counts as a real fast; the ring marks it and − stops at it. */
const MINIMUM_REAL_FAST_MS = 12 * HOUR_IN_MS
const DISPLAY_MODE_STORAGE_KEY = 'timerDisplayMode'
const CURRENT_FAST_STORAGE_KEY = 'currentFast'
const FAST_HISTORY_STORAGE_KEY = 'fastHistory'
const DISPLAY_MODE_ELAPSED = 'elapsed'
const DISPLAY_MODE_REMAINING = 'remaining'
const FAT_BURN_THRESHOLD_SECONDS = 8 * 60 * 60
const KCAL_PER_GRAM_FAT = 9.4
const DEFAULT_DAILY_KCAL = 2000
const FAT_FRACTION_MIN = 0.2
const FAT_FRACTION_MAX = 0.9
const FAT_BURN_TAU_HOURS = 16
const DEFAULT_PROGRESS_PHASE = {
   key: 'blue',
   maxMs: 8 * HOUR_IN_MS,
   color: 'var(--progress-fill-blue, #38bdf8)',
   background: 'var(--progress-track-blue, #0f172a)',
} as const
const PROGRESS_PHASES = [
   DEFAULT_PROGRESS_PHASE,
   {
      key: 'green',
      maxMs: 14 * HOUR_IN_MS,
      color: 'var(--progress-fill-green, #22c55e)',
      background: 'var(--progress-track-green, #052e16)',
   },
   {
      key: 'orange',
      maxMs: 24 * HOUR_IN_MS,
      color: 'var(--progress-fill-orange, #f97316)',
      background: 'var(--progress-track-orange, #431407)',
   },
   {
      key: 'red',
      maxMs: Number.POSITIVE_INFINITY,
      color: 'var(--progress-fill-red, #ef4444)',
      background: 'var(--progress-track-red, #450a0a)',
   },
] as const

export type CurrentFastDisplayMode = 'elapsed' | 'remaining'
export type ProgressPhaseKey = (typeof PROGRESS_PHASES)[number]['key']

export type CurrentFastEntry = {
   startTime: number
   duration: number
   durationUpdatedAt: number
   notifiedCompletion: boolean
   displayMode: CurrentFastDisplayMode
}

export type FastHistoryEntry = {
   startTime: number
   endTime: number
   intendedDuration: number
   historyId?: number | null
}

export type HistoryRemoteState = {
   loading: boolean
   hasRemoteSync: boolean
   errorMessage: string
}

type BroadcastAction =
   | 'start-fast'
   | 'end-fast'
   | 'adjust-duration'
   | 'toggle-display-mode'
   | 'edit-fast'
   | 'delete-fast'

const FASTING_MILESTONES = [
   {
      id: 'sugar-spike-over',
      titleKey: 'Sugar Spike Over',
      hours: 2,
      messageKey:
         "🔥 Your post-meal blood sugar peak is behind you. Insulin is busy putting that energy into storage, and soon you'll start drawing it back out.",
   },
   {
      id: 'housekeeper-wave',
      titleKey: 'Housekeeper Wave',
      hours: 4,
      messageKey:
         "🔥 Your stomach is emptying and your gut's “housekeeper” has started. It's a strong muscular wave that sweeps your digestive tract clean every 90–120 minutes, and it only runs when you're not eating. Any snack stops it.",
   },
   {
      id: 'hunger-wave',
      titleKey: 'That Growl Is Cleanup',
      hours: 6,
      messageKey:
         "🔥 Felt a hunger pang? Studies show the cleaning wave itself triggers hunger, through a hormone called motilin. Your tank isn't empty, your gut is just doing its housekeeping. It passes.",
   },
   {
      id: 'fat-release',
      titleKey: 'Fat Stores Unlocked',
      hours: 8,
      messageKey:
         '🔥 Insulin is back to its fasting level. Insulin is the main brake on fat release, so with the brake off your fat cells start releasing stored fat into your bloodstream.',
   },
   {
      id: 'liver-takes-over',
      titleKey: 'Your Liver Takes Over',
      hours: 10,
      messageKey:
         "🔥 Nothing is coming in from food, so your liver now keeps your whole body's blood sugar steady from its own reserves.",
   },
   {
      id: 'metabolic-switch',
      titleKey: 'Metabolic Switch',
      hours: 12,
      messageKey:
         '🔥 Fat in your blood is rising and your body is shifting its fuel mix toward fat. This is the start of the “metabolic switch” fasting researchers talk about.',
   },
   {
      id: 'gluconeogenesis',
      titleKey: 'Sugar From Scratch',
      hours: 14,
      messageKey:
         "🔥 Your liver is now making about half of your blood sugar from scratch (gluconeogenesis). Part of the raw material is the backbone of the fat you're burning (glycerol).",
   },
   {
      id: 'ketones-online',
      titleKey: 'Ketones Online',
      hours: 16,
      messageKey:
         "🔥 Your liver is turning fat into ketones, now several times higher than after a meal. Your heart and muscles take them up eagerly. Nice work, that's a full 16:8!",
   },
   {
      id: 'running-on-fat',
      titleKey: 'Running on Fat',
      hours: 18,
      messageKey:
         '🔥 Fat is now your main fuel, and over half of your blood sugar is made fresh by your liver. Hunger comes in waves around your usual mealtimes and fades on its own.',
   },
   {
      id: 'growth-hormone-surge',
      titleKey: 'Growth Hormone Surge',
      hours: 24,
      messageKey:
         "🔥 Studies show growth hormone up several-fold after a day, helping protect your muscle. Your immune cells' inflammation switch is also turned down.",
   },
   {
      id: 'ketosis',
      titleKey: 'Ketosis',
      hours: 36,
      messageKey:
         '🔥 Ketones are climbing into true ketosis. In animal studies, cellular recycling (autophagy) rises sharply with fasts this long.',
   },
   {
      id: 'metabolism-revving',
      titleKey: 'Metabolism Revving Up',
      hours: 48,
      messageKey:
         '🔥 Forget “starvation mode”. Studies show your metabolism speeding up in the first days, driven by noradrenaline, the hormone of alertness. Your brain is now running partly on ketones.',
   },
   {
      id: 'three-day-fast',
      titleKey: 'Three-Day Fast',
      hours: 72,
      messageKey:
         "🔥 Ketones can reach 2–4 mmol/L. In lab studies they also act as messengers that switch on your cells' stress defenses. Fasts this long are best done with medical guidance and electrolytes.",
   },
] as const

const [selectedDurationHours, setSelectedDurationHours] = createSignal('16')
const [startTimeInputValue, setStartTimeInputValue] = createSignal('')
const [currentFast, setCurrentFast] = createSignal<CurrentFastEntry | null>(null)
const [fastHistory, setFastHistory] = createSignal<FastHistoryEntry[]>([])
const [displayMode, setDisplayMode] =
   createSignal<CurrentFastDisplayMode>(DISPLAY_MODE_ELAPSED)
const [currentFastError, setCurrentFastError] = createSignal('')
const [historyMonthsDisplayed, setHistoryMonthsDisplayed] = createSignal(1)
const historyEdit = createEditModalState(['start', 'end'] as const)
const isEditHistoryModalOpen = historyEdit.isOpen
const editingHistoryIndex = historyEdit.editingIndex
const editingHistoryStartValue = historyEdit.valueAccessor('start')
const editingHistoryEndValue = historyEdit.valueAccessor('end')
const historyEditError = historyEdit.error
const setHistoryEditError = historyEdit.setError
const [historyRemoteState, setHistoryRemoteState] = createSignal<HistoryRemoteState>({
   loading: false,
   hasRemoteSync: false,
   errorMessage: '',
})
const [startupSyncLoading, setStartupSyncLoading] = createSignal(false)

/**
 * Returns whether the code is running in a browser environment.
 *
 * @returns True when `window` is available.
 */
function isBrowser() {
   return typeof window !== 'undefined'
}

/**
 * Validates a timer display-mode value (`elapsed`/`remaining`).
 *
 * @param value - The candidate mode.
 * @returns The normalized mode, or null when unrecognized.
 */
function normalizeDisplayMode(value: unknown): CurrentFastDisplayMode | null {
   if (value === DISPLAY_MODE_REMAINING) {
      return DISPLAY_MODE_REMAINING
   }
   if (value === DISPLAY_MODE_ELAPSED) {
      return DISPLAY_MODE_ELAPSED
   }
   return null
}

/**
 * Validates and normalizes a stored current-fast entry.
 *
 * @param value - The raw stored value.
 * @returns The current-fast entry, or null when invalid.
 */
function normalizeCurrentFastEntry(value: unknown): CurrentFastEntry | null {
   if (!value || typeof value !== 'object') {
      return null
   }

   const candidate = value as Partial<CurrentFastEntry>
   const startTime = candidate.startTime
   const duration = candidate.duration
   if (
      typeof startTime !== 'number' ||
      !Number.isFinite(startTime) ||
      typeof duration !== 'number' ||
      !Number.isFinite(duration) ||
      duration <= 0
   ) {
      return null
   }

   return {
      startTime,
      duration,
      durationUpdatedAt:
         (
            typeof candidate.durationUpdatedAt === 'number' &&
            Number.isFinite(candidate.durationUpdatedAt)
         ) ?
            candidate.durationUpdatedAt
         :  startTime,
      notifiedCompletion: candidate.notifiedCompletion === true,
      displayMode: normalizeDisplayMode(candidate.displayMode) ?? DISPLAY_MODE_ELAPSED,
   }
}

/**
 * Validates and normalizes a stored fast-history entry.
 *
 * @param value - The raw stored value.
 * @returns The history entry, or null when invalid.
 */
function normalizeFastHistoryEntry(value: unknown): FastHistoryEntry | null {
   if (!value || typeof value !== 'object') {
      return null
   }

   const candidate = value as Partial<FastHistoryEntry>
   const startTime = candidate.startTime
   const endTime = candidate.endTime
   if (
      typeof startTime !== 'number' ||
      !Number.isFinite(startTime) ||
      typeof endTime !== 'number' ||
      !Number.isFinite(endTime) ||
      endTime <= startTime
   ) {
      return null
   }

   return {
      startTime,
      endTime,
      intendedDuration:
         (
            typeof candidate.intendedDuration === 'number' &&
            Number.isFinite(candidate.intendedDuration)
         ) ?
            candidate.intendedDuration
         :  endTime - startTime,
      historyId:
         (
            typeof candidate.historyId === 'number' &&
            Number.isInteger(candidate.historyId)
         ) ?
            candidate.historyId
         :  null,
   }
}

/**
 * Normalizes, de-duplicates (by start/end), and sorts fast-history entries newest first.
 *
 * @param entries - The raw entries.
 * @returns The cleaned, sorted history entries.
 */
function dedupeFastHistoryEntries(entries: unknown[]): FastHistoryEntry[] {
   const seen = new Set<string>()
   const result: FastHistoryEntry[] = []

   for (const entry of entries) {
      const normalized = normalizeFastHistoryEntry(entry)
      if (!normalized) {
         continue
      }
      const key = `${normalized.startTime}:${normalized.endTime}`
      if (seen.has(key)) {
         continue
      }
      seen.add(key)
      result.push(normalized)
   }

   return result.sort((a, b) => b.startTime - a.startTime)
}

/**
 * Persists the current fast, history, and display mode to localStorage.
 */
function persistState() {
   if (!isBrowser()) {
      return
   }

   window.localStorage.setItem(CURRENT_FAST_STORAGE_KEY, JSON.stringify(currentFast()))
   window.localStorage.setItem(FAST_HISTORY_STORAGE_KEY, JSON.stringify(fastHistory()))
   window.localStorage.setItem(DISPLAY_MODE_STORAGE_KEY, displayMode())
}

/**
 * Dispatches a runtime event describing the latest current-fast state change.
 *
 * @param action - The action that triggered the change.
 * @param historyChange - Optional details of an added/edited/deleted history entry.
 */
function broadcastState(
   action: BroadcastAction,
   historyChange?: CurrentFastStateDetail['historyChange'],
) {
   dispatchCurrentFastStateChange({
      action,
      currentFast: currentFast(),
      fastHistory: fastHistory(),
      displayMode: displayMode(),
      historyChange,
   })
}

/**
 * Returns whether a window overlaps any recorded fast in history.
 *
 * @param startTime - Window start in epoch ms.
 * @param endTime - Window end in epoch ms.
 * @returns True when the window overlaps existing history.
 */
function hasFastOverlap(startTime: number, endTime: number): boolean {
   return fastHistory().some(
      entry => startTime < entry.endTime && endTime > entry.startTime,
   )
}

/**
 * Returns whether a window overlaps the active fast or history, ignoring one history index (for edits).
 *
 * @param startTime - Window start in epoch ms.
 * @param endTime - Window end in epoch ms.
 * @param ignoreIndex - History index to skip, or null.
 * @returns True when the window is invalid or overlaps another fast.
 */
function hasFastOverlapAtIndex(
   startTime: number,
   endTime: number,
   ignoreIndex: number | null,
): boolean {
   if (!Number.isFinite(startTime) || !Number.isFinite(endTime) || endTime <= startTime) {
      return true
   }

   const activeFast = currentFast()
   if (activeFast) {
      const activeFastEndTime = activeFast.startTime + activeFast.duration
      if (startTime < activeFastEndTime && endTime > activeFast.startTime) {
         return true
      }
   }

   return fastHistory().some((entry, index) => {
      if (ignoreIndex !== null && index === ignoreIndex) {
         return false
      }
      return startTime < entry.endTime && endTime > entry.startTime
   })
}

/**
 * Computes cumulative grams of fat oxidized since a fast started, using an exponential model.
 *
 * @param secondsSinceStart - Seconds elapsed since the fast began.
 * @param dailyKcal - Assumed daily kcal expenditure.
 * @returns Cumulative fat grams.
 */
function computeCumulativeFatBurned(
   secondsSinceStart: number,
   dailyKcal: number,
): number {
   const safeSeconds = Math.max(0, secondsSinceStart)
   const tHours = safeSeconds / 3600
   const kcalPerHour = dailyKcal / 24
   const expTerm = Math.exp(-tHours / FAT_BURN_TAU_HOURS)
   const cumulativeFatKcal =
      kcalPerHour *
      (FAT_FRACTION_MIN * tHours +
         (FAT_FRACTION_MAX - FAT_FRACTION_MIN) *
            (tHours - FAT_BURN_TAU_HOURS * (1 - expTerm)))
   return cumulativeFatKcal / KCAL_PER_GRAM_FAT
}

/**
 * Hydrates the current-fast store (display mode, current fast, history) from localStorage on startup.
 */
export function initializeCurrentFastState() {
   if (!isBrowser()) {
      return
   }

   const storedDisplayMode = normalizeDisplayMode(
      window.localStorage.getItem(DISPLAY_MODE_STORAGE_KEY),
   )
   const resolvedDisplayMode = storedDisplayMode ?? DISPLAY_MODE_ELAPSED
   setDisplayMode(resolvedDisplayMode)

   try {
      const parsedCurrentFast = JSON.parse(
         window.localStorage.getItem(CURRENT_FAST_STORAGE_KEY) ?? 'null',
      )
      const normalizedCurrentFast = normalizeCurrentFastEntry(parsedCurrentFast)
      if (normalizedCurrentFast) {
         normalizedCurrentFast.displayMode = resolvedDisplayMode
      }
      setCurrentFast(normalizedCurrentFast)
   } catch {
      setCurrentFast(null)
   }

   try {
      const parsedHistory = JSON.parse(
         window.localStorage.getItem(FAST_HISTORY_STORAGE_KEY) ?? '[]',
      )
      const normalizedHistory =
         Array.isArray(parsedHistory) ? dedupeFastHistoryEntries(parsedHistory) : []
      setFastHistory(normalizedHistory)
   } catch {
      setFastHistory([])
   }

   setCurrentFastError('')
   setHistoryEditError('')
}

/**
 * Sets the selected fast-duration input (in hours).
 *
 * @param nextValue - The raw input value.
 */
export function setFastDurationHours(nextValue: string) {
   setSelectedDurationHours(nextValue)
}

/**
 * Sets the fast start-time input value.
 *
 * @param nextValue - The raw input value.
 */
export function setFastStartTimeInputValue(nextValue: string) {
   setStartTimeInputValue(nextValue)
}

/**
 * Clears the current-fast error message.
 */
export function clearCurrentFastError() {
   setCurrentFastError('')
}

/**
 * Sets the current-fast error message from a sync failure.
 *
 * @param message - The error text to display.
 */
export function setCurrentFastSyncError(message: string) {
   setCurrentFastError(message)
}

/**
 * Replaces fast history from the legacy app layer and persists it.
 *
 * @param entries - The raw history entries.
 */
export function syncFastHistoryFromLegacy(entries: unknown[]) {
   const normalizedEntries =
      Array.isArray(entries) ? dedupeFastHistoryEntries(entries) : []
   setFastHistory(normalizedEntries)
   persistState()
}

/**
 * Applies current-fast, display-mode, and loading updates pushed from the legacy app layer.
 *
 * @param nextState - Partial state to apply (currentFast/displayMode/startupSyncLoading).
 */
export function syncCurrentFastStateFromLegacy(nextState: {
   currentFast?: unknown
   displayMode?: unknown
   startupSyncLoading?: boolean
}) {
   if (Object.prototype.hasOwnProperty.call(nextState, 'currentFast')) {
      const normalizedDisplayMode =
         normalizeDisplayMode(nextState.displayMode) ?? displayMode()
      const normalizedCurrentFast = normalizeCurrentFastEntry(nextState.currentFast)
      if (normalizedCurrentFast) {
         normalizedCurrentFast.displayMode = normalizedDisplayMode
      }
      setCurrentFast(normalizedCurrentFast)
   }

   if (Object.prototype.hasOwnProperty.call(nextState, 'displayMode')) {
      const normalizedDisplayMode = normalizeDisplayMode(nextState.displayMode)
      if (normalizedDisplayMode) {
         setDisplayMode(normalizedDisplayMode)
      }
   }

   if (typeof nextState.startupSyncLoading === 'boolean') {
      setStartupSyncLoading(nextState.startupSyncLoading)
   }
}

/**
 * Applies remote history-sync status (loading/hasRemoteSync/error) from the legacy app layer.
 *
 * @param nextState - Partial remote-history state to apply.
 */
export function syncHistoryRemoteStateFromLegacy(nextState: Partial<HistoryRemoteState>) {
   const currentState = historyRemoteState()
   setHistoryRemoteState({
      loading: nextState.loading === true,
      hasRemoteSync:
         typeof nextState.hasRemoteSync === 'boolean' ?
            nextState.hasRemoteSync
         :  currentState.hasRemoteSync,
      errorMessage:
         typeof nextState.errorMessage === 'string' ? nextState.errorMessage : '',
   })
}

/**
 * Starts a fast from the selected duration and start-time inputs, validating overlap.
 *
 * @returns True when the fast started, false on validation failure.
 */
function startFast() {
   const durationHours = Number.parseInt(selectedDurationHours(), 10)
   const startTime =
      startTimeInputValue() ? new Date(startTimeInputValue()).getTime() : Date.now()

   if (!Number.isFinite(durationHours) || durationHours <= 0) {
      setCurrentFastError(translate('Select a valid duration before starting a fast.'))
      return false
   }

   if (!Number.isFinite(startTime)) {
      setCurrentFastError(translate('Select a valid start time before starting a fast.'))
      return false
   }

   const duration = durationHours * HOUR_IN_MS
   const projectedEndTime = startTime + duration
   if (hasFastOverlap(startTime, projectedEndTime)) {
      setCurrentFastError(
         translate(
            'This fast overlaps with a previously recorded fast. Adjust the start time or duration.',
         ),
      )
      return false
   }

   const nextFast: CurrentFastEntry = {
      startTime,
      duration,
      durationUpdatedAt: Date.now(),
      notifiedCompletion: false,
      displayMode: displayMode(),
   }

   setCurrentFast(nextFast)
   setStartTimeInputValue('')
   setCurrentFastError('')
   persistState()
   broadcastState('start-fast')
   return true
}

/**
 * Ends the active fast, recording it to history when it lasted long enough.
 *
 * @returns True when a fast was ended, false when none was active.
 */
function endFast() {
   const activeFast = currentFast()
   if (!activeFast) {
      return false
   }

   const endTime = Date.now()
   const actualDuration = Math.max(0, endTime - activeFast.startTime)
   let nextHistory = fastHistory()

   if (actualDuration > 8 * HOUR_IN_MS) {
      const nextEntry: FastHistoryEntry = {
         startTime: activeFast.startTime,
         endTime,
         intendedDuration: activeFast.duration,
         historyId: null,
      }
      nextHistory = dedupeFastHistoryEntries([nextEntry, ...nextHistory])
      setFastHistory(nextHistory)
   }

   setCurrentFast(null)
   setCurrentFastError('')
   persistState()
   broadcastState('end-fast')
   return true
}

/**
 * Starts or ends the fast depending on whether one is active.
 *
 * @returns True when the start/end action succeeded.
 */
export function toggleFast() {
   return currentFast() ? endFast() : startFast()
}

/**
 * Adjusts the active fast's planned duration by a number of hours (clamped to a minimum).
 *
 * @param hoursDelta - Hours to add (may be negative).
 */
export function adjustFastDuration(hoursDelta: number) {
   const activeFast = currentFast()
   if (!activeFast || !Number.isFinite(hoursDelta) || hoursDelta === 0) {
      return
   }

   // Shortening never goes below the 12 h floor; a shorter target set elsewhere stays as is.
   const floor =
      hoursDelta < 0 ?
         Math.min(activeFast.duration, MINIMUM_REAL_FAST_MS)
      :  MIN_ACTIVE_FAST_DURATION_MS
   const nextDuration = Math.max(
      floor,
      activeFast.duration + hoursDelta * FAST_DURATION_STEP_MS,
   )

   if (nextDuration === activeFast.duration) {
      return
   }

   setCurrentFast({
      ...activeFast,
      duration: nextDuration,
      durationUpdatedAt: Date.now(),
   })
   setCurrentFastError('')
   persistState()
   broadcastState('adjust-duration')
}

/**
 * Returns whether the active fast's target can still be shortened (it is above 12 h).
 *
 * @returns True when the − button should be enabled.
 */
export function canReduceFastDuration() {
   const activeFast = currentFast()
   return activeFast !== null && activeFast.duration > MINIMUM_REAL_FAST_MS
}

/**
 * Returns the ring angle of the 12-hour mark for a fast target, measured clockwise from
 * the top like the progress conic-gradient.
 *
 * @param targetMs - The displayed fast target in milliseconds, or null without a fast.
 * @returns The angle in degrees, or null without a target, while the startup sync is
 *    loading, or when the target is shorter than 12 h.
 */
export function getMinimumFastMarkerAngle(targetMs: number | null): number | null {
   if (
      targetMs === null ||
      startupSyncLoading() ||
      !Number.isFinite(targetMs) ||
      targetMs < MINIMUM_REAL_FAST_MS
   ) {
      return null
   }
   return (MINIMUM_REAL_FAST_MS / targetMs) * 360
}

/**
 * Toggles the timer between elapsed and remaining display modes.
 */
export function toggleDisplayMode() {
   const nextDisplayMode =
      displayMode() === DISPLAY_MODE_REMAINING ? DISPLAY_MODE_ELAPSED : (
         DISPLAY_MODE_REMAINING
      )
   setDisplayMode(nextDisplayMode)
   const activeFast = currentFast()
   if (activeFast) {
      setCurrentFast({
         ...activeFast,
         displayMode: nextDisplayMode,
      })
   }
   persistState()
   broadcastState('toggle-display-mode')
}

/**
 * Increases the number of history months shown and notifies the runtime to load more.
 */
export function requestMoreHistoryMonths() {
   const nextMonths = historyMonthsDisplayed() + 1
   setHistoryMonthsDisplayed(nextMonths)
   dispatchHistoryMonthsChange(nextMonths)
}

/**
 * Opens the edit modal for a history entry, pre-filling its start/end inputs.
 *
 * @param index - The history entry index to edit.
 */
export function openEditHistoryModal(index: number) {
   const entry = fastHistory()[index]
   if (!entry) {
      return
   }

   historyEdit.open(index, {
      start: formatDateTimeLocalValue(entry.startTime),
      end: formatDateTimeLocalValue(entry.endTime),
   })
}

export const closeEditHistoryModal = historyEdit.close
export const setEditingHistoryStartInputValue = (value: string) =>
   historyEdit.setValue('start', value)
export const setEditingHistoryEndInputValue = (value: string) =>
   historyEdit.setValue('end', value)

/**
 * Validates and saves the edited history entry, checking for overlaps.
 *
 * @returns True when saved, false on validation failure.
 */
export function saveEditedHistoryEntry() {
   const index = editingHistoryIndex()
   const startTime = new Date(editingHistoryStartValue()).getTime()
   const endTime = new Date(editingHistoryEndValue()).getTime()

   if (
      index === null ||
      index < 0 ||
      index >= fastHistory().length ||
      Number.isNaN(startTime) ||
      Number.isNaN(endTime)
   ) {
      setHistoryEditError(translate('Enter valid start and end times.'))
      return false
   }

   if (endTime <= startTime) {
      setHistoryEditError(translate('End time must be after start time.'))
      return false
   }

   if (hasFastOverlapAtIndex(startTime, endTime, index)) {
      setHistoryEditError(
         translate(
            'Updated fast overlaps with another recorded fast. Adjust the times and try again.',
         ),
      )
      return false
   }

   const nextHistory = [...fastHistory()]
   const existingEntry = nextHistory[index]
   if (!existingEntry) {
      return false
   }

   const updatedEntry: FastHistoryEntry = {
      ...existingEntry,
      startTime,
      endTime,
      intendedDuration: existingEntry.intendedDuration,
   }
   nextHistory[index] = updatedEntry
   setFastHistory(dedupeFastHistoryEntries(nextHistory))
   persistState()
   broadcastState('edit-fast', {
      type: 'edit',
      entry: updatedEntry,
   })
   closeEditHistoryModal()
   return true
}

/**
 * Removes a history entry by index and broadcasts the deletion.
 *
 * @param index - The history entry index to delete.
 */
export function deleteHistoryEntry(index: number) {
   if (index < 0 || index >= fastHistory().length) {
      return
   }

   const nextHistory = [...fastHistory()]
   const [removedEntry] = nextHistory.splice(index, 1)
   if (!removedEntry) {
      return
   }

   setFastHistory(dedupeFastHistoryEntries(nextHistory))
   persistState()
   broadcastState('delete-fast', {
      type: 'delete',
      historyId: removedEntry.historyId ?? null,
   })
}

/**
 * Formats a millisecond duration as `HH:MM:SS`.
 *
 * @param ms - The duration in milliseconds.
 * @returns The formatted duration string.
 */
export function formatDuration(ms: number): string {
   const safeMilliseconds = Number.isFinite(ms) ? Math.max(0, ms) : 0
   const totalSeconds = Math.floor(safeMilliseconds / 1000)
   const hours = Math.floor(totalSeconds / 3600)
      .toString()
      .padStart(2, '0')
   const minutes = Math.floor((totalSeconds % 3600) / 60)
      .toString()
      .padStart(2, '0')
   const seconds = (totalSeconds % 60).toString().padStart(2, '0')
   return `${hours}:${minutes}:${seconds}`
}

/**
 * Formats a timestamp as a short localized date-time for history rows.
 *
 * @param timestamp - The epoch-ms timestamp.
 * @returns The localized string.
 */
export function formatFastHistoryTimestamp(timestamp: number): string {
   return new Date(timestamp).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
   })
}

/**
 * Formats a timestamp as a localized month-and-year label.
 *
 * @param timestamp - The epoch-ms timestamp.
 * @returns The label, or empty when invalid.
 */
export function formatHistoryMonthLabel(timestamp: number): string {
   const date = new Date(timestamp)
   if (Number.isNaN(date.getTime())) {
      return ''
   }
   return date.toLocaleDateString(undefined, {
      month: 'long',
      year: 'numeric',
   })
}

/**
 * Returns a `YYYY-MM` grouping key for a timestamp.
 *
 * @param timestamp - The epoch-ms timestamp.
 * @returns The month key, or empty when invalid.
 */
export function getHistoryMonthKey(timestamp: number): string {
   const date = new Date(timestamp)
   if (Number.isNaN(date.getTime())) {
      return ''
   }
   return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
}

/**
 * Returns the epoch-ms start of midnight N months ago for history filtering.
 *
 * @param months - Months to look back.
 * @returns The cutoff timestamp.
 */
export function getHistoryCutoffTimestamp(months: number): number {
   const safeMonths = Number.isFinite(months) ? Math.max(0, Math.round(months)) : 1
   const anchor = new Date()
   anchor.setHours(0, 0, 0, 0)
   anchor.setMonth(anchor.getMonth() - safeMonths)
   return anchor.getTime()
}

/**
 * Returns a fast's duration in milliseconds from its start/end, duration, or intended duration.
 *
 * @param entry - The fast or history entry, if any.
 * @returns The duration in milliseconds (0 when unavailable).
 */
export function getFastDurationMs(
   entry: Partial<FastHistoryEntry> | CurrentFastEntry | null,
): number {
   if (!entry || typeof entry !== 'object') {
      return 0
   }

   const endTime = (entry as Partial<FastHistoryEntry>).endTime
   if (
      typeof entry.startTime === 'number' &&
      typeof endTime === 'number' &&
      endTime > entry.startTime
   ) {
      return endTime - entry.startTime
   }
   if (typeof (entry as CurrentFastEntry).duration === 'number') {
      return (entry as CurrentFastEntry).duration
   }
   if (typeof (entry as Partial<FastHistoryEntry>).intendedDuration === 'number') {
      return (entry as Partial<FastHistoryEntry>).intendedDuration ?? 0
   }
   return 0
}

/**
 * Builds a history-shaped snapshot of the active fast up to a given time.
 *
 * @param now - The reference end time (defaults to now).
 * @returns The snapshot, or null when no fast is active.
 */
export function getCurrentFastSnapshot(now = Date.now()): FastHistoryEntry | null {
   const activeFast = currentFast()
   if (!activeFast) {
      return null
   }

   return {
      startTime: activeFast.startTime,
      endTime: now,
      intendedDuration: activeFast.duration,
      historyId: null,
   }
}

/**
 * Formats a fast's projected end as a relative day-and-time phrase (e.g. `Fast ends tomorrow at 08:00`).
 *
 * @param timestamp - The projected end time in epoch ms.
 * @param remainingMs - Milliseconds remaining (0 or less means already ended).
 * @returns The localized phrase, or empty when invalid.
 */
export function formatFastEndTimestamp(timestamp: number, remainingMs: number): string {
   const date = new Date(timestamp)
   if (Number.isNaN(date.getTime())) {
      return ''
   }

   const hours = String(date.getHours()).padStart(2, '0')
   const minutes = String(date.getMinutes()).padStart(2, '0')
   const now = new Date()
   const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate())
   const endDayStart = new Date(date.getFullYear(), date.getMonth(), date.getDate())
   const dayDiff = Math.round(
      (endDayStart.getTime() - todayStart.getTime()) / (24 * HOUR_IN_MS),
   )

   let dayLabel = translate('today')
   if (dayDiff === 1) {
      dayLabel = translate('tomorrow')
   } else if (dayDiff > 1) {
      dayLabel = translate('in {count} days', { count: dayDiff })
   } else if (dayDiff === -1) {
      dayLabel = translate('yesterday')
   } else if (dayDiff < -1) {
      dayLabel = translate('{count} days ago', { count: Math.abs(dayDiff) })
   }

   const template =
      remainingMs <= 0 ?
         'Fast ended {dayLabel} at {time}'
      :  'Fast ends {dayLabel} at {time}'
   return translate(template, {
      dayLabel,
      time: `${hours}:${minutes}`,
   })
}

/**
 * Formats a duration as a compact target-hours label (e.g. `16h`).
 *
 * @param durationMs - The duration in milliseconds.
 * @returns The label.
 */
export function formatTargetHours(durationMs: number): string {
   const totalHours = Math.max(0, durationMs / HOUR_IN_MS)
   const roundedHours =
      Number.isInteger(totalHours) ? totalHours.toString() : totalHours.toFixed(1)
   return `${roundedHours}h`
}

/**
 * Formats grams of fat burned for display, switching to kilograms above 1000 g.
 *
 * @param grams - The grams value.
 * @returns The formatted string.
 */
export function formatFatBurnDisplay(grams: number): string {
   if (!Number.isFinite(grams) || grams < 0) {
      return '0 g'
   }
   if (grams === 0) {
      return '0 g'
   }
   if (grams >= 1000) {
      const kilograms = Math.floor(grams / 1000)
      const remainingGrams = Math.floor(grams % 1000)
      return remainingGrams === 0 ?
            `${kilograms} kg`
         :  `${kilograms} kg ${remainingGrams} g`
   }
   if (grams >= 100) {
      return `${grams.toFixed(1)} g`
   }
   return `${grams.toFixed(2)} g`
}

/**
 * Returns grams of fat burned for an elapsed fast time, above the fat-burning baseline.
 *
 * @param elapsedMs - Elapsed fast time in milliseconds.
 * @returns Grams of fat burned (never negative).
 */
export function getFatBurnedGrams(elapsedMs: number): number {
   const seconds = Math.max(0, elapsedMs / 1000)
   const burned = computeCumulativeFatBurned(seconds, DEFAULT_DAILY_KCAL)
   const baseline = computeCumulativeFatBurned(
      FAT_BURN_THRESHOLD_SECONDS,
      DEFAULT_DAILY_KCAL,
   )
   return Math.max(0, burned - baseline)
}

/**
 * Returns the two most recently reached fasting milestones for an elapsed time.
 *
 * @param elapsedMs - Elapsed fast time in milliseconds.
 * @returns The reached milestones, newest first (up to two).
 */
export function getMilestonesForElapsed(elapsedMs: number) {
   const safeElapsed = Math.max(0, elapsedMs)
   return FASTING_MILESTONES.filter(
      milestone => safeElapsed >= milestone.hours * HOUR_IN_MS,
   )
      .slice(-2)
      .reverse()
}

/**
 * Returns the progress phase (color/background) for an elapsed fast time.
 *
 * @param elapsedMs - Elapsed fast time in milliseconds.
 * @returns The matching progress phase.
 */
function getProgressPhaseForElapsed(elapsedMs: number) {
   const safeElapsed = Number.isFinite(elapsedMs) ? Math.max(0, elapsedMs) : 0
   return (
      PROGRESS_PHASES.find(phase => safeElapsed < phase.maxMs) ?? DEFAULT_PROGRESS_PHASE
   )
}

/**
 * Returns the progress-phase key for an elapsed fast time.
 *
 * @param elapsedMs - Elapsed fast time in milliseconds.
 * @returns The phase key.
 */
export function getProgressPhaseKeyForElapsed(elapsedMs: number): ProgressPhaseKey {
   return getProgressPhaseForElapsed(elapsedMs).key
}

/**
 * Builds the conic-gradient CSS for the progress ring, colored by fasting phase.
 *
 * @param progressRatio - Completion ratio in [0,1].
 * @param elapsedMs - Elapsed fast time in milliseconds.
 * @returns The `conic-gradient(...)` CSS value.
 */
export function getProgressCircleBackground(
   progressRatio: number,
   elapsedMs: number,
): string {
   const clampedRatio = Math.max(0, Math.min(progressRatio, 1))
   const totalAngle =
      clampedRatio > 0 ? Math.max(0, Math.min(360, clampedRatio * 360)) : 360
   const phase = getProgressPhaseForElapsed(elapsedMs)

   if (elapsedMs <= 0) {
      return `conic-gradient(${DEFAULT_PROGRESS_PHASE.color} 0deg ${totalAngle}deg, ${DEFAULT_PROGRESS_PHASE.background} ${totalAngle}deg 360deg)`
   }

   const elapsedForRatio = Math.max(elapsedMs, 1)
   let accumulatedAngle = 0
   let previousThreshold = 0
   const segments: string[] = []

   for (const phaseDef of PROGRESS_PHASES) {
      const phaseMax = phaseDef.maxMs
      const cappedElapsed =
         phaseMax === Number.POSITIVE_INFINITY ? elapsedMs : Math.min(elapsedMs, phaseMax)
      const phaseElapsed = Math.max(0, cappedElapsed - previousThreshold)
      previousThreshold = phaseMax

      if (phaseElapsed <= 0) {
         continue
      }

      const fraction = phaseElapsed / elapsedForRatio
      const segmentAngle = Math.min(totalAngle - accumulatedAngle, totalAngle * fraction)
      const startAngle = accumulatedAngle
      accumulatedAngle += segmentAngle
      segments.push(`${phaseDef.color} ${startAngle}deg ${accumulatedAngle}deg`)

      if (accumulatedAngle >= totalAngle - 0.1) {
         break
      }
   }

   if (accumulatedAngle < totalAngle) {
      segments.push(`${phase.background} ${accumulatedAngle}deg ${totalAngle}deg`)
   }

   return `conic-gradient(${segments.join(', ')}, ${phase.background} ${totalAngle}deg, ${phase.background} 360deg)`
}

export {
   currentFast,
   currentFastError,
   displayMode,
   editingHistoryEndValue,
   editingHistoryStartValue,
   fastHistory,
   historyEditError,
   historyMonthsDisplayed,
   historyRemoteState,
   isEditHistoryModalOpen,
   selectedDurationHours,
   startTimeInputValue,
   startupSyncLoading,
}
